import { shallowEqual, useDispatch, useSelector } from 'umi';
import { useEffect, useMemo } from 'react';
import { useRequest } from 'ahooks';
import http from '@/utils/http';
import { RequrstBack } from '@/pages/aigc/types/server';
import { queryInitStatClientConfig } from '@/pages/aigc/constants/server';
import { ADD_ANALYSIS, ADD_DASHBOARD, gettingStartList, LinkType, SHARE_DASHBOARD, } from '../GettingStart';
import { useSearchParamsState } from '@/pages/aigc/hooks/useQuery';
import { isArray, isEmpty } from 'lodash-es';
import { namespace } from '../../Multidimensional/Analysis/model';
import { DateGranularity } from '@dbl/core/date';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import { notification } from 'tea-component';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { ADD_ANALYSIS_DEMO_LIST, ADD_DASHBOARD_DEMO_LIST, SHARE_DASHBOARD_DEMO_LIST, } from '../constants';
import { getNewDataTable } from '@dbl/services/dataSource';
import { storage } from '@/utils/utils';

type ICommonProps<S = string> = {
  [key in Readonly<LinkType>]: {
    // 请求地址
    fetchUrl?: S;
    // 请求入参,请求的入参是分固定以及不固定的
    fetchParams?: unknown;
    // 缓存key，详见ahooks说明
    cacheKey: S;
    // 默认数据兜底情况
    defaultData: unknown;
  };
};

export const useGetDataLabGuideDataList = () => {
  // 获取code
  const {isShowGuide} = useIsShowGuide();
  const {gameCode, allPrivileges} = useSelector((state: any) => ({
    gameCode: state.datalab.gameCode,
    allPrivileges: state.privilegesConfig.allPrivileges,
  }), shallowEqual);
  // 获取指引标识
  const [subTab] = useSearchParamsState('helpType', '');
  const dispatch = useDispatch();
  const { isEn } = useDblIntl('home');

  const {
    data: newDataTable,
  } = useRequest(() =>
    getNewDataTable({
      game_code: gameCode,
      keywords: 'game_metric_datalab',
      order_by: 'table_name',
      desc: true,
      page: 1,
      page_size: 20,
    }),{
    ready: !!subTab && subTab === ADD_ANALYSIS
  }
  );
  
  // 获取各模块指引入参以及请求地址
  const commonParamsConfig: Partial<ICommonProps> = useMemo(
    () => ({
      [ADD_DASHBOARD]: {
        cacheKey: ADD_DASHBOARD,
        defaultData: subTab ? ADD_DASHBOARD_DEMO_LIST : [],
      },
      [ADD_ANALYSIS]: {
        fetchUrl: '/api/v1/datalab/batchGetTableInfo',
        fetchParams: {
          table_id: [newDataTable?.data_list?.[0]?.table_id]
        },
        cacheKey: ADD_ANALYSIS,
        defaultData: subTab ? ADD_ANALYSIS_DEMO_LIST : [],
      },
      [SHARE_DASHBOARD]: {
        cacheKey: SHARE_DASHBOARD,
        defaultData: subTab ? SHARE_DASHBOARD_DEMO_LIST : [],
      },
    }),
    [subTab, newDataTable?.data_list],
  );
  const {
    fetchUrl = '',
    fetchParams = {},
    // cacheKey = '',
    defaultData = [],
  } = useMemo(() => {
    if (subTab) {
      return commonParamsConfig?.[subTab as string];
    }
    return {};
  }, [commonParamsConfig, subTab]);
  const fetch = () =>
    http({
      url: fetchUrl,
      method: 'post',
      data: fetchParams,
    }) as RequrstBack;
  const data = useRequest(fetch, {
    ...queryInitStatClientConfig,
    refreshDeps: [subTab],
    // cacheKey,
    loadingDelay: 300,
    ready: !!subTab &&  !isEmpty(newDataTable?.data_list) && subTab === ADD_ANALYSIS ,
    retryCount: 0,
  });
  useEffect(() => {
    if ((!isEmpty(data?.data) || defaultData)  && !isEmpty(newDataTable?.data_list) &&  subTab === ADD_ANALYSIS && isShowGuide ) {
      const [joinRelations, joinTables] = getTableInfosAndTableJoinRelations(
        !isEmpty(data?.data) ? data?.data : defaultData,
      );
      dispatch({
        type: `${namespace}/save`,
        payload: {
          dataTableJoinRelations: joinRelations,
          isOpenSelfJoin: false,
          mode: ChartCalculateMode.Aggregate,
          columnRawColumns: [],
        },
      });
      dispatch({
        type: `${namespace}/setTableInfo`,
        payload: { response: joinTables },
      });
      dispatch({
        type: `${namespace}/filterConditionByTable`,
        payload: {
          table_ids: joinTables.map((item) => item.table_id),
          tables: joinTables,
        },
      });
      return () => {
        if (isEmpty(data?.data) && subTab === ADD_ANALYSIS) {
          dispatch({
            type: `${namespace}/save`,
            payload: {
              dataTableJoinRelations: [],
              isOpenSelfJoin: false,
              mode: ChartCalculateMode.Aggregate,
              columnRawColumns: [],
            },
          });
          dispatch({
            type: `${namespace}/setTableInfo`,
            payload: { response: [] },
          });
          dispatch({
            type: `${namespace}/filterConditionByTable`,
            payload: {
              table_ids: [],
              tables: []
            },
          });
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data, subTab, defaultData, isShowGuide]);
  
  useEffect(
    () => () => {
      if (subTab) {
        const { privileges } =
        gettingStartList.find((task) => task.key === subTab) || {};
        const hasPrivilege = privileges?.some((item: string) =>
          allPrivileges?.includes(item),
        );
        if (!hasPrivilege) {
          // 弹出提示
          notification.error({
            description: isEn ? 'No Permission' : '无权限',
          });
        }
      }
    },
    [allPrivileges, defaultData, isEn, subTab],
  );
  
  return {
    ...data,
    isShowGuide,
    defaulet: defaultData,
    guideSubTab: subTab
  };
};

const getTableInfosAndTableJoinRelations = (
  dataList: Map<any, any> | Set<any> | List<any> | null | undefined,
) => {
  if (!isArray(dataList) || isEmpty(dataList)) return [];
  const dataSource = dataList || [];
  const joinRelations = dataSource.map(
    (task: {
      table_name: string;
      table_id: string;
      table_code: string;
      cycle: string;
    }) => ({
      ...task,
      granularity: task?.cycle ?? DateGranularity.DAY,
      connect_type: '',
      on: [],
    }),
  );
  const joinTables = dataSource.map((task) => ({
    ...task,
    only_one_table: true,
    table_code: `t${0}`,
  }));
  return [joinRelations, joinTables];
};

// 最近分享,模板中心
export const useGetHomeTemplateData = <T,>(params?: T, ready?: boolean) => {
  const fetch = () =>
    http({
      url: '/api/v1/datalab/home',
      method: 'post',
      data: params,
    }) as RequrstBack;
  const data = useRequest(fetch, {
    // ...queryInitStatClientConfig,
    debounceWait: 600,
    refreshDeps: [params],
    ready: !ready.current && params?.tab
  });
  return {
    ...data,
  };
};

// 获取报表列表创建人
export const useGetDashBoardCreatorData = <T,>(params: T, ready=true) => {
  const fetch = () =>
    http({
      url: 'api/v1/datalab/custom_report/creators',
      method: 'post',
      data: params,
    }) as RequrstBack;
  const data = useRequest(fetch, {
    ...queryInitStatClientConfig,
    // refreshDeps: [params],
    cacheKey: 'templateDashboard',
    retryCount: 0,
    ready,
    manual: true,
  });
  return {
    ...data,
  };
};

// 获取
export const useGetHomeTabCount = <T,>(params?: T,onSuccess?: (...args: any[])=>void) => {
  const fetch = (params) =>
    http({
      url: 'api/v1/datalab/get_tab_count',
      method: 'post',
      data: params,
    }) as RequrstBack;
  
  const data = useRequest((newParams) => fetch(newParams ?? params), {
    ...queryInitStatClientConfig,
    // refreshDeps: [params?.game_code],
    // cacheKey: 'templateCount',
    retryCount: 0,
    onSuccess
  });
  return {
    ...data,
  };
};

// 判断是否展示guide
export const useIsShowGuide = () => {
  // const [subTab] = useSearchParamsState('helpType', '');
  const { userInfo } = useSelector(state => 
    // @ts-ignore
    ({
      userInfo: state?.home?.userInfo,
    })
  );
  const { userid } = userInfo || {};
  const userList = storage.getItem('__dbl_guide_notShow_list');
  const noShowList = JSON.parse(userList) || {};
  // 需求变更: 只要点击过某个页面的新手指引的 Skip 按钮, 所有页面的新手指引下一次都不会显示
  // 所以这里兼容旧数据, 只要有该用户id, 就代表该用户点击过某个页面的新手指引的 Skip 按钮, 所以所有页面的新手指引下一次都不会显示
  const isShowGuide = !noShowList[userid];

  return {
    isShowGuide,
    noShowList,
    userid,
  };
};